/**
 * Defence 2 before it runs: the batches this campaign will be sent in, shown at
 * the moment somebody is deciding whether to send it.
 *
 * ## Why a plan is worth showing rather than just doing
 *
 * A campaign that quietly sends 102 of 1,278 and then appears to stall is a
 * campaign somebody cancels and re-creates, which is the worst possible outcome
 * — it re-materialises the list and starts the ramp again. The waits are the
 * product working, and a wait nobody was told about reads as a fault. So the
 * whole shape is on the screen before the button, with the size of the first
 * batch and the length of the first wait in it.
 *
 * ## Every number here is the plan's own
 *
 * Nothing on this panel is computed. `SEED_WAVE_SIZE`, the growth factor and the
 * hold minutes are all decisions `warmUpPlanFor` already took against the health
 * verdict, and re-deriving any of them here would produce a screen that can
 * disagree with the plan that was actually written to `campaign_send_waves`.
 * `plannedForRecipients` is printed as the plan's own denominator for the same
 * reason: a plan is stale exactly when that number stops matching the list, and
 * a panel that substituted the current reach would hide the disagreement it
 * exists to reveal.
 *
 * **The last batch carries no wait**, which is not a special case in the data —
 * every wave has `holdMinutes` — but is one on the screen: "then waits 30
 * minutes" under the final batch describes a wait for a batch that does not
 * exist, and a reader who counts it adds half an hour to their estimate of when
 * the campaign finishes.
 *
 * ## The small-list branch says what still applies, not only what does not
 *
 * `warmUpNotNeededSentence` is the contract's, and it is written the way it is
 * on purpose: "no batches" on its own reads as "no protection". The halt in
 * defence 3 takes no plan and no size, so it applies at every volume, and the
 * stop sentence sits under **both** branches rather than only under the plan.
 *
 * ## AND IT SAYS *WHICH* NUMBER PUT THE CAMPAIGN UNDER THE FLOOR
 *
 * A plan is sized on what the campaign will **attempt**, which is the reachable
 * list less suppressions and less everything the send guard refuses. In a
 * guarded workspace that is 1 out of 526 — and the flat sentence this panel used
 * to render then said *"this list is small enough to send in one go"* about a
 * list of 526 people. The list is not small; it is filtered, and those are two
 * different things to tell somebody about their own data.
 *
 * So both counts are props and the contract picks the sentence from them. This
 * panel does not name the send guard: `SendGuardNotice` says that on the same
 * screen with this campaign's own numbers in it, and two accounts of one fact in
 * two tones is exactly what the heading rule in `list-safety-display.ts`
 * forbids.
 *
 * That stop sentence comes from `sendRiskStopSentence(gate)` and the gate comes
 * from `sendRiskGateFor(channel)`. This panel never sees a threshold or a noun
 * separately, so it cannot pair email's 2% with WhatsApp's word or the reverse —
 * the defect this repository has already shipped once, in the other direction.
 */
import { warmUpNotNeededSentence, sendRiskGateFor, type Channel, type WarmUpPlan } from '@wend/shared';
import { Badge, Kicker } from '@wend/ui';

import { formatCount, pluralise } from '../../lib/format';
import { formatHoldWait, sendRiskStopSentence } from '../../lib/list-safety-display';

export interface WarmUpPlanPanelProps {
  /** `null` when the attempted count is below the floor — never an empty wave list. */
  plan: WarmUpPlan | null;
  channel: Channel;
  /** What `plan` was sized from: `listSafety.willAttempt`, never the reach. */
  willAttempt: number;
  /** `listSafety.warmUpEnforced`: false means there are no batches at any size. */
  warmUpEnforced: boolean;
  /** Everybody this channel can reach — `health.signals.total`. The other half of the sentence. */
  reachable: number;
}

export function WarmUpPlanPanel({
  plan,
  channel,
  willAttempt,
  reachable,
  warmUpEnforced,
}: WarmUpPlanPanelProps) {
  const stopSentence = sendRiskStopSentence(sendRiskGateFor(channel));

  /*
   * NO PLAN, NO PANEL — SLICE 28. The owner quoted this one first.
   *
   * With batching switched off in a deployment, this rendered a heading and two
   * paragraphs whose entire content was that a feature the sender never asked
   * for is not running, plus the halt threshold as a policy statement. Three
   * accounts of one silence, and the same silence is explained again 300px lower
   * by the results panel.
   *
   * A sender cannot turn batching on: `WARM_UP_ENFORCED` is a deployment
   * setting. Naming a switch somebody cannot reach, to explain the absence of
   * something they did not want, is the clearest case there is of writing to the
   * wrong reader.
   *
   * **The panel still draws whenever a plan exists**, with its waves, its counts
   * and its stop sentence — because then it describes what is about to happen to
   * this campaign, which is the sender's subject. `warmUpNotNeededSentence` and
   * `sendRiskStopSentence` both stay in `list-safety-display` for the operator
   * view of the deployment.
   */
  if (plan === null) return null;

  const lastWaveNumber = plan.waves[plan.waves.length - 1]?.waveNumber ?? 0;

  return (
    <section className="flex flex-col gap-3" aria-label="The batch plan">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Kicker as="span">
          The batch plan
        </Kicker>
        <Badge tone="info">
          {pluralise(plan.waves.length, 'batch', 'batches')} · {formatCount(plan.plannedForRecipients)}{' '}
          recipients
        </Badge>
      </div>

      <ol className="flex flex-col">
        {plan.waves.map((wave) => (
          <li
            key={wave.waveNumber}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-line py-2.5 first:border-t-0 first:pt-0"
          >
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              Batch {String(wave.waveNumber)}
            </span>
            <span className="text-[1.05rem] font-semibold tabular-nums text-text">
              {formatCount(wave.plannedSize)}
            </span>
            <span className="text-[0.85rem] text-muted">
              {/* The final batch has nothing after it to wait for. */}
              {wave.waveNumber === lastWaveNumber
                ? pluralise(wave.plannedSize, 'recipient')
                : `${pluralise(wave.plannedSize, 'recipient')}, then at least ${formatHoldWait(wave.holdMinutes)} before the next`}
            </span>
          </li>
        ))}
      </ol>

      <p className="max-w-prose text-[0.82rem] leading-relaxed text-muted">{stopSentence}</p>
    </section>
  );
}
